// Passe da Temporada (lote 3, item 14): 30 níveis de 10 estrelas, com a trilha grátis e a do assinante lado a lado.
// Grátis e Bronze veem a trilha do assinante com cadeado e o atalho para Planos; ela não é vendida à parte. No fim de
// partida, as estrelas que a partida deu (GA4 passe_nivel quando sobe). Tudo calculado no servidor; aqui só mostra.
import { useEffect, useRef, useState } from 'react';
import { CATALOGO_DE_COSMETICOS, type PainelPasse, type PasseNoFim, type PremioNaTela } from '@truco6/gamificacao';
import { lerPasse } from '../comum/api.ts';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { medirUmaVez } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { Tela, Topo } from '../telas/comum.tsx';
import { Icone } from '../telas/planos-comum.tsx';
import { Tento } from '../telas/tentos-comum.tsx';
import { Previa } from './colecao.tsx';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
// '2026-T4' vira "outubro a dezembro de 2026".
export function nomeDaTemporada(t: string): string {
  const m = /^(\d{4})-T([1-4])$/.exec(t);
  if (!m) return t;
  const i = (Number(m[2]) - 1) * 3;
  return `${MESES[i]} a ${MESES[i + 2]} de ${m[1]}`;
}

function Premio({ p, sessao, perfil, travado }: { p: PremioNaTela; sessao: Sessao; perfil: PerfilConta; travado: boolean }) {
  let corpo: React.ReactNode, nome: string;
  if (p.tipo === 'cosmetico' && p.cosmetico) {
    const c = CATALOGO_DE_COSMETICOS.find((x) => x.id === p.cosmetico!.id);
    nome = `${p.cosmetico.nome} (${p.cosmetico.categoria === 'mesa' ? 'mesa' : p.cosmetico.categoria === 'verso' ? 'verso' : 'moldura'})`;
    corpo = <>{c && <Previa x={c} sessao={sessao} perfil={perfil} tamanho={30} />}<span>{p.cosmetico.nome}</span></>;
  } else if (p.tipo === 'tentos') {
    nome = `${p.valor} Tentos`;
    corpo = <><Tento tamanho={18} /><span>{p.valor} Tentos</span></>;
  } else if (p.tipo === 'descanso') {
    nome = `${p.valor} descanso da ofensiva`;
    corpo = <><i aria-hidden="true">💤</i><span>+{p.valor} descanso</span></>;
  } else {
    nome = `${p.valor} de Rodagem`;
    corpo = <span>+{p.valor} <small>Rodagem</small></span>;
  }
  return (
    <div className={'premio-passe' + (p.pago ? ' pago' : '') + (travado ? ' travado' : '')} aria-label={nome + (p.pago ? ', recebido' : travado ? ', com cadeado' : '')}>
      {corpo}
      {p.pago && <b className="check" aria-hidden="true">✓</b>}
      {travado && <b className="trava" aria-hidden="true">🔒</b>}
    </div>
  );
}

export function PasseTela({ sessao, perfil }: { sessao: Sessao; perfil: PerfilConta }) {
  const [p, setP] = useState<PainelPasse | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const atual = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    let vivo = true;
    lerPasse(sessao).then((x) => { if (vivo) setP(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id]);
  // O nível atual aparece sem esconder o título: a tela só rola se ele estiver fora da vista.
  useEffect(() => { atual.current?.scrollIntoView({ block: 'nearest' }); }, [p?.nivel]);
  const noNivel = p ? (p.nivel >= p.niveis ? p.estrelasPorNivel : p.estrelas % p.estrelasPorNivel) : 0;
  return (
    <Tela larga>
      <Topo titulo="Passe da Temporada" voltar={voltar} />
      <div className="passe-tela">
        <div className="passe-lado">
          {erro && <p className="erro" role="alert">{erro}</p>}
          {!p && !erro && <p className="carregando" role="status">Um instante...</p>}
          {p && (
            <section className="cartao passe-cab" aria-label="Seu passe">
              <small className="rotulo dourado">TEMPORADA DE {nomeDaTemporada(p.temporada).toUpperCase()}</small>
              <div className="passe-nivel">
                <span className="selo-passe" aria-hidden="true">{p.nivel}</span>
                <p><b>Nível {p.nivel} de {p.niveis}</b><small>{p.nivel >= p.niveis ? 'Passe completo!' : `${noNivel} de ${p.estrelasPorNivel} estrelas para o nível ${p.nivel + 1}`}</small></p>
              </div>
              <span className="trilho-passe" role="progressbar" aria-label={`Estrelas do nível: ${noNivel} de ${p.estrelasPorNivel}`}
                aria-valuemin={0} aria-valuemax={p.estrelasPorNivel} aria-valuenow={noNivel}>
                <i style={{ width: `${Math.round((noNivel / p.estrelasPorNivel) * 100)}%` }} />
              </span>
              <p className="nota">Estrelas: 1 por partida terminada, 2 por missão do dia e 5 por missão da semana.</p>
              <button className="link" onClick={() => ir({ tela: 'missoes' })}>Ver missões</button>
            </section>
          )}
          {p && !p.assinante && (
            <section className="cartao passe-assine" aria-label="Trilha do assinante">
              <p><b>A trilha do assinante vem com o Prata e o Ouro.</b> Cosméticos da temporada, descansos extras e mais Rodagem. Quem assina libera na hora o que já alcançou.</p>
              <button className="btn2 largo" onClick={() => ir({ tela: 'planos', plano: 'prata', ciclo: 'mensal' })}>Ver planos</button>
            </section>
          )}
        </div>
        {p && (
          <div className="passe-lado">
            <div className="trilhas-cab" aria-hidden="true"><span /><b>GRÁTIS</b><b>ASSINANTE{p.assinante ? '' : ' 🔒'}</b></div>
            <ol className="trilhas" aria-label="Níveis do passe">
              {p.trilhas.map((t) => (
                <li key={t.nivel} ref={t.nivel === Math.min(p.niveis, p.nivel + 1) ? atual : undefined}
                  className={t.nivel <= p.nivel ? 'alcancado' : t.nivel === p.nivel + 1 ? 'proximo' : undefined} aria-label={`Nível ${t.nivel}`}>
                  <span className="num">{t.nivel}</span>
                  <div>{t.gratis.map((x, i) => <Premio key={i} p={x} sessao={sessao} perfil={perfil} travado={false} />)}</div>
                  <div>{t.assinante.map((x, i) => <Premio key={i} p={x} sessao={sessao} perfil={perfil} travado={!p.assinante} />)}</div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </Tela>
  );
}

// Fim de partida: as estrelas da partida e o nível do passe.
export function PasseDaPartida({ x, partida }: { x: PasseNoFim; partida: string }) {
  const subiu = x.nivel.depois > x.nivel.antes;
  useEffect(() => { if (subiu) medirUmaVez(`passe:${partida}`, 'passe_nivel', { nivel: x.nivel.depois }); }, [subiu, partida]);
  const noNivel = x.nivel.depois >= x.niveis ? x.estrelasPorNivel : x.total % x.estrelasPorNivel;
  return (
    <button className="cartao passe-fim" onClick={() => ir({ tela: 'passe' })} aria-label={`Passe da Temporada: mais ${x.estrelas} estrelas, nível ${x.nivel.depois}`}>
      <span className="estrelas-fim" aria-hidden="true">⭐ +{x.estrelas}</span>
      <span className="passe-fim-meio">
        <b>{subiu ? `Passe: nível ${x.nivel.depois}!` : `Passe da Temporada · nível ${x.nivel.depois}`}</b>
        <span className="trilho-passe"><i style={{ width: `${Math.round((noNivel / x.estrelasPorNivel) * 100)}%` }} /></span>
      </span>
      <Icone nome="seta" tamanho={16} />
    </button>
  );
}

// No perfil e nas missões: o atalho para o passe.
export function PasseNoPerfil() {
  return (
    <button className="cartao linha-plano" onClick={() => ir({ tela: 'passe' })}>
      <span className="emoji-linha" aria-hidden="true">⭐</span>
      <span><b>Passe da Temporada</b><small>30 níveis, trilha grátis e do assinante</small></span>
      <Icone nome="seta" tamanho={18} />
    </button>
  );
}
